import { Box, Dialog, FormControl } from '@mui/material';
import { pathOr, isEmpty } from 'ramda';
import React, { KeyboardEventHandler } from 'react';
import { connect } from 'react-redux';
import * as GA from 'spw-shared/analytics';
import { Dispatch, bindActionCreators } from 'redux';
import {
  GROWTH_CALCULATORS,
  PROJECT_TYPE_TO_PATH,
  PROJECTS_ROUTE,
  TOTAL_PROGRAM_TYPE,
} from 'spw-constants';
import {
  selectCurrentScenario,
  selectScenarios,
  selectTotalPrograms,
} from 'spw-redux/modules/calculators/selectors';
import {
  saveScenario as saveScenarioAction,
  cloneScenario as cloneScenarioAction,
  saveUpdatingScenario as saveUpdatingScenarioAction,
  setIsEditingSavedScenario as setIsEditingSavedScenarioAction,
  setScenarioDialogStatus as setScenarioDialogStatusAction,
  updateScenario as updateScenarioAction,
  setScenarioValue as setScenarioValueAction,
  setUpdateScenarioDialog as setUpdateScenarioDialogAction,
  createTotalProgram as createTotalProgramAction,
} from 'spw-redux/modules/calculators/actions';
import { IAppState } from 'spw-redux/reducer';
import {
  SPWBaseSelect,
  SPWCancelButton88Width,
  SPWDialogActions,
  SPWSaveButton88Width,
} from 'spw-shared/globalComponents';
import {
  CalculatorType,
  ITotalProgram,
  Scenario,
} from 'spw-redux/modules/calculators/types';
import { setCurrentView as setCurrentViewAction } from 'spw-redux/modules/building-detail/actions';
import SelectShrunkenLabel from 'spw-shared/globalComponents/select-shrunked-label';
import { IUser } from 'spw-redux/modules/user/types';
import {
  IBuilding,
  IDepartment,
  IFloor,
  IProject,
  IProjectColors,
} from 'spw-redux/modules/projects/types';
import { getProposedCloneName, handleOnKeyPress } from 'spw-shared/utils';
import {
  ID_TYPE,
  setSummarySelectedIdType as setSummarySelectedIdTypeAction,
} from 'spw-redux/modules/program-summary/actions';
import {
  getScenarioListLocation,
  isTotalProgram as isTotalProgramFunction,
} from 'spw-components/programming-scenarios/building-hs-sc-scenario/utils';
import { isMacro } from 'spw-components/worksheet/utils';
import { colors } from 'spw-theme';
import { RenameScenarioDialog } from 'spw-components/dialogs/save-scenario/styles';
import { selectProjectDepartments } from 'spw-redux/modules/projects/selectors';
import { withRouter, RouteComponentProps } from 'react-router-v5';
import {
  PROGRAMMING_BUILDING_VIEWS,
  SCENARIO_LIST_TYPES,
} from 'spw-components/project-details-tabs/building-details/constants';
import { Configuration } from './configuration';
import CopyNotes from './copy-notes';
import { ScenarioName } from './name';

import {
  DialogForm,
  ScenarioLegend,
  SimpleUSFDialogTitle,
  SaveScenarioDialogContent,
} from '../styles';
import { PlainProgramScenario } from '../../../models/Scenario';

export const NAME_STEP = 'NAME_STEP';

export enum SaveAsOperation {
  copy = 'copy',
  copyWithDifferentCalculator = 'copyWithDifferentCalculator',
}

interface ISaveScenario {
  project: IProject;
  showDialog: boolean;
  scenarios: PlainProgramScenario[];
  building: IBuilding;
  currentScenario: PlainProgramScenario;
  setCurrentView: (newView: string) => void;
  user: IUser;
  duplicated: boolean;
  isUpdate?: boolean;
  step?: string;
  setScenarioDialogStatus: (
    status: boolean,
    duplicated?: boolean,
    step?: string | null
  ) => void;
  setUpdateScenarioDialog: (isUpdate: boolean) => void;
  saveScenario: (options: {
    scenarioName: string;
    scenarioNote: string;
    shouldCopyNotes: boolean;
    isDetailed: boolean;
    isShared: boolean;
    floorId?: number;
    departmentId?: number;
    redirect?: (scenario: PlainProgramScenario) => void;
  }) => void;
  cloneScenario: (options: {
    scenarioId: number;
    name: string;
    isDetailed: boolean;
    isShared: boolean;
    shouldCopyNotes: boolean;

    optionsForCopy?: {
      scenarioFloorId?: number;
      scenarioDepartmentId?: number;
    };
    optionsForCopyWithDifferentCalculator?: {
      calculatorType?: CalculatorType;
      department?: IDepartment;
      floor?: IFloor;
      selectedFloorUSF?: number;
      selectedBuildingUSF?: number;
    };
    redirect?: (scenario: Scenario) => void;
  }) => void;
  setScenarioValue: ({
    value,
    factor,
  }: {
    value: number | string;
    factor: string;
  }) => void;
  setIsEditingSavedScenario: (val: boolean) => void;
  setSummarySelectedIdType: (
    id: IFloor['id'] | IBuilding['id'],
    selectedType: string
  ) => void;
  updateScenario: (
    scenario: PlainProgramScenario,
    changes: Partial<Scenario>
  ) => void;
  saveUpdatingScenario: (val?: boolean) => void;
  projectColors: IProjectColors;
  totalPrograms: PlainProgramScenario[];
  createTotalProgram: typeof createTotalProgramAction;
  departments?: IDepartment[];
}

interface SaveScenarioState {
  scenarioName: string;
  scenarioNote: string;
  selectedFloor: IFloor;
  selectedFloorUSF?: number;
  selectedBuildingUSF?: number;
  isClosing: boolean;
  isDetailed: boolean;
  isShared: boolean;
  shouldCopyNotes: boolean;
  saveAsOperation: SaveAsOperation;
  calculatorType?: CalculatorType;
  selectedDepartment?: IDepartment;
  dialogIsOpen: boolean;
}

class SaveScenario extends React.Component<
  ISaveScenario & RouteComponentProps,
  SaveScenarioState
> {
  private _mounted: boolean;

  constructor(props: ISaveScenario & RouteComponentProps) {
    super(props);
    this.state = {
      scenarioName: '',
      scenarioNote: '',
      selectedFloor: null,
      isClosing: false,
      isDetailed: false,
      isShared: false,
      shouldCopyNotes: false,
      saveAsOperation: SaveAsOperation.copy,
      dialogIsOpen: false,
    };

    this.handleSave = this.handleSave.bind(this);
    this.handleOnClose = this.handleOnClose.bind(this);
    this.onKeyPressed = this.onKeyPressed.bind(this);
    this.cleanData = this.cleanData.bind(this);
    this.handleFloorChange = this.handleFloorChange.bind(this);
    this.setScenarioName = this.setScenarioName.bind(this);
    this.setScenarioFloorId = this.setScenarioFloorId.bind(this);
  }

  componentDidMount() {
    this._mounted = true;
    document.addEventListener('keydown', this.onKeyPressed);
    const { currentScenario, duplicated, isUpdate } = this.props;

    if (currentScenario) {
      const { isDetailed } = this.state;
      let { isShared } = this.state;
      if (isMacro(currentScenario) && !currentScenario.id) {
        isShared = true;
      }

      if (currentScenario.floorId) {
        this.setState({
          selectedFloor: this.findFloor(currentScenario.floorId),
        });
      }

      if (isUpdate) {
        this.setState({
          scenarioName: currentScenario.name,
        });
      } else if (duplicated) {
        this.setState({
          scenarioName: this.cloneScenarioName(currentScenario.name),
        });
      }

      this.setState({
        isDetailed: currentScenario.isDetailed || isDetailed,
        isShared: currentScenario.isShared || isShared,
      });
    }
  }

  static getDerivedStateFromProps(
    nextProps: ISaveScenario,
    prevState: SaveScenarioState
  ): Partial<SaveScenarioState> {
    const { currentScenario, departments, duplicated } = nextProps;
    let newState: Partial<SaveScenarioState> = {};
    if (
      currentScenario?.departmentId &&
      !prevState.selectedDepartment &&
      departments
    ) {
      newState = {
        selectedDepartment: departments.find(
          (d) => currentScenario.departmentId === d.id
        ),
      };
    }
    if (
      currentScenario &&
      currentScenario.name !== prevState.scenarioName &&
      duplicated &&
      prevState.dialogIsOpen === false &&
      nextProps.showDialog
    ) {
      const { scenarios, totalPrograms } = nextProps;
      const scenarioNames = [...scenarios, ...totalPrograms]?.map(
        (scenario) => scenario.name
      );
      newState = {
        ...newState,
        scenarioName: getProposedCloneName(currentScenario.name, scenarioNames),
      };
    }
    if (nextProps.showDialog !== prevState.dialogIsOpen) {
      newState = {
        ...newState,
        dialogIsOpen: nextProps.showDialog,
        isClosing: !nextProps.showDialog,
      };
    }
    return Object.keys(newState).length ? newState : null;
  }

  componentDidUpdate(prevProps: Readonly<ISaveScenario>) {
    const { showDialog, currentScenario, isUpdate } = this.props;
    const { selectedFloor } = this.state;

    if (showDialog !== prevProps.showDialog && showDialog) {
      if (isUpdate) {
        this.setScenarioName(currentScenario.name);
      }

      if (
        currentScenario?.floorId &&
        currentScenario.floorId !== selectedFloor?.id
      ) {
        this.setScenarioFloorId(this.findFloor(currentScenario.floorId));
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKeyPressed);
    this._mounted = false;
  }

  handleFloorChange(floor: IFloor) {
    this.setState({
      selectedFloor: floor,
    });
  }

  handleSaveAndName() {
    const { currentScenario, duplicated, setScenarioDialogStatus } = this.props;

    this.setState({
      scenarioName: duplicated
        ? this.cloneScenarioName(currentScenario.name)
        : '',
    });
    setScenarioDialogStatus(true, true, NAME_STEP);
  }

  handleSave() {
    const {
      currentScenario,
      setScenarioDialogStatus,
      saveScenario,
      cloneScenario,
      setIsEditingSavedScenario,
      isUpdate,
      setSummarySelectedIdType,
      updateScenario,
      saveUpdatingScenario,
      duplicated,
      user,
      createTotalProgram,
      project,
      history,
    } = this.props;
    const {
      scenarioName,
      scenarioNote,
      selectedFloor,
      isDetailed,
      isShared,
      shouldCopyNotes,
      calculatorType,
      selectedDepartment,
      saveAsOperation,
      selectedFloorUSF,
      selectedBuildingUSF,
    } = this.state;
    if (!this.isScenarioNameRepeated(scenarioName)) {
      this.setState({ isClosing: true });
      setScenarioDialogStatus(false);

      if (!isUpdate) {
        if (isTotalProgramFunction(currentScenario)) {
          createTotalProgram(
            {
              name: scenarioName,
              notes: ((currentScenario as unknown) as ITotalProgram).notes,
            },
            {
              redirect: () => {
                const { DEPARTMENTS } = SCENARIO_LIST_TYPES;
                const { SCENARIOS } = PROGRAMMING_BUILDING_VIEWS;
                history.replace(
                  `${PROJECTS_ROUTE}/${PROJECT_TYPE_TO_PATH[project.type]}/${
                    project.id
                  }/building/${SCENARIOS}?type=${DEPARTMENTS}`
                );
              },
              scenarioIdsToLink: currentScenario.linkedScenarios.map(
                (tps) => tps.id
              ),
            }
          );
        } else {
          if (selectedFloor) {
            setSummarySelectedIdType(selectedFloor.id, ID_TYPE.floorId);
          }

          if (duplicated) {
            cloneScenario({
              scenarioId: currentScenario.id,
              name: scenarioName,
              isDetailed,
              isShared:
                calculatorType === GROWTH_CALCULATORS.macroProgramming ||
                saveAsOperation === SaveAsOperation.copy
                  ? isShared
                  : false,

              shouldCopyNotes,
              optionsForCopy:
                saveAsOperation === SaveAsOperation.copy
                  ? {
                      scenarioFloorId: selectedFloor?.id,
                      scenarioDepartmentId: selectedDepartment?.id,
                    }
                  : null,
              optionsForCopyWithDifferentCalculator:
                saveAsOperation === SaveAsOperation.copyWithDifferentCalculator
                  ? {
                      calculatorType,
                      department: selectedDepartment,
                      floor: selectedFloor,
                      selectedFloorUSF,
                      selectedBuildingUSF,
                    }
                  : undefined,
              redirect: (scenario: Scenario) => {
                history.replace(getScenarioListLocation(project, scenario));
              },
            });
          } else {
            saveScenario({
              scenarioName,
              scenarioNote,
              shouldCopyNotes,
              floorId: selectedFloor?.id,
              departmentId: selectedDepartment?.id,
              isDetailed,
              isShared,
              redirect: (scenario: PlainProgramScenario) => {
                history.replace(
                  `${PROJECTS_ROUTE}/${PROJECT_TYPE_TO_PATH[project.type]}/${
                    project.id
                  }/building/${PROGRAMMING_BUILDING_VIEWS.SCENARIO_DETAILS}/${
                    scenario.id
                  }`
                );
              },
            });
          }
          setIsEditingSavedScenario(false);
        }
      } else {
        updateScenario(currentScenario, {
          name: scenarioName,
          note: scenarioNote,
          floorId: selectedFloor?.id,
          department: selectedDepartment,
          isDetailed,
          isShared,
        });
        saveUpdatingScenario(true);
      }

      GA.event({
        category: 'Programming',
        action: duplicated
          ? GA.tagFromDupCalculator(currentScenario.calculatorType)
          : GA.tagFromSaveCalculator(currentScenario.calculatorType),
        label: JSON.stringify({
          projectType: 'Programming',
          userId: user.id,
        }),
        subCategory: duplicated
          ? GA.dupCategoryFromCalculator(currentScenario.calculatorType)
          : GA.saveCategoryFromCalculator(currentScenario.calculatorType),
      });

      this.handleOnClose();
    }
  }

  handleOnClose() {
    const { setScenarioDialogStatus, setUpdateScenarioDialog } = this.props;
    this.cleanData();
    setScenarioDialogStatus(false);
    setUpdateScenarioDialog(false);
  }

  onKeyPressed(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.handleSave();
      e.preventDefault();
      e.stopPropagation();
    }
  }

  setScenarioName(name: string) {
    this.setState({
      scenarioName: name,
    });
  }

  setScenarioFloorId(floor: IFloor) {
    this.setState({
      selectedFloor: floor,
    });
  }

  private cloneScenarioName = (name: string): string => {
    const { scenarios, totalPrograms } = this.props;

    const scenarioNames = [...scenarios, ...totalPrograms].map(
      (scenario) => scenario.name
    );
    return getProposedCloneName(name, scenarioNames);
  };

  saveAsWithDifferentCalculatorFormIsValid = () => {
    const {
      saveAsOperation,
      calculatorType,
      selectedFloor,
      selectedDepartment,
      selectedFloorUSF,
      selectedBuildingUSF,
    } = this.state;
    const { building } = this.props;
    if (saveAsOperation === SaveAsOperation.copy) return true;
    if (!calculatorType) return false;
    if (
      calculatorType ===
        GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments &&
      !selectedDepartment
    )
      return false;
    if (
      [
        GROWTH_CALCULATORS.floorHeadcountSeatcount,
        GROWTH_CALCULATORS.floorUsf,
      ].includes(calculatorType) &&
      !selectedFloor
    )
      return false;
    const floorUSFIsRequired =
      calculatorType === GROWTH_CALCULATORS.floorUsf &&
      !selectedFloor.totalFloorUsf;
    if (floorUSFIsRequired && !selectedFloorUSF) return false;
    const buildingUSFISRequired =
      calculatorType === GROWTH_CALCULATORS.buildingUsf &&
      !building?.programmingUsf;
    if (buildingUSFISRequired && !selectedBuildingUSF) return false;
    return true;
  };

  findFloor = (floorId: number) => {
    const { building } = this.props;
    return building?.floors?.find((f) => f.id === floorId);
  };

  cleanData() {
    const { currentScenario } = this.props;
    if (this._mounted) {
      this.setState({
        scenarioName: '',
        scenarioNote: '',
        selectedFloor: this.findFloor(currentScenario?.floorId),
      });
    }
  }

  isScenarioNameRepeated(newName: string) {
    const { isUpdate, scenarios, currentScenario, totalPrograms } = this.props;
    const list = scenarios.concat(totalPrograms);
    const scenariosExcludingCurrentScenario = list.filter((s) =>
      isUpdate ? s.id !== currentScenario?.id : true
    );
    return scenariosExcludingCurrentScenario.some(
      ({ name }: { name: string }) =>
        name.toLowerCase().trim() === newName.toLowerCase().trim()
    );
  }

  render() {
    const {
      showDialog,
      building,
      currentScenario,
      isUpdate,
      step,
      project,
      departments,
      duplicated,
    } = this.props;

    const {
      scenarioName,
      isClosing,
      isDetailed,
      isShared,
      shouldCopyNotes,
      saveAsOperation,
      calculatorType,
      selectedDepartment,
      selectedFloor,
      selectedFloorUSF,
      selectedBuildingUSF,
    } = this.state;

    const isScenarioNameRepeated = this.isScenarioNameRepeated(scenarioName);

    const disabled =
      isEmpty(scenarioName) ||
      isScenarioNameRepeated ||
      !this.saveAsWithDifferentCalculatorFormIsValid();

    if (!showDialog) return null;
    const setScenarioName = (val: string) =>
      this.setState({ scenarioName: val });

    let configurationEle = (
      <Configuration
        project={project}
        scenario={currentScenario}
        isDetailed={isDetailed}
        setIsDetailed={(val: boolean) => this.setState({ isDetailed: val })}
        isShared={isShared}
        setIsShared={(val: boolean) => this.setState({ isShared: val })}
        saveAsOperation={saveAsOperation}
        setSaveAsOperation={(op) => this.setState({ saveAsOperation: op })}
        calculatorType={calculatorType}
        setCalculatorType={(c) => this.setState({ calculatorType: c })}
        setSelectedDepartment={(dept) =>
          this.setState({ selectedDepartment: dept })
        }
        selectedDepartment={selectedDepartment}
        selectedFloor={selectedFloor}
        setSelectedFloor={(floor) => this.setState({ selectedFloor: floor })}
        selectedFloorUSF={selectedFloorUSF}
        setSelectedFloorUSF={(usf) => this.setState({ selectedFloorUSF: usf })}
        selectedBuildingUSF={selectedBuildingUSF}
        setSelectedBuildingUSF={(usf) =>
          this.setState({ selectedBuildingUSF: usf })
        }
      />
    );

    const nameEle = (
      <ScenarioName
        scenarioName={scenarioName}
        setScenarioName={(val) => setScenarioName(val)}
        disabled={isScenarioNameRepeated}
        isClosing={isClosing}
      />
    );

    if (step === NAME_STEP) {
      return (
        <Dialog
          open={showDialog}
          onClose={this.handleOnClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <SimpleUSFDialogTitle id="alert-dialog-title" className="big-padding">
            Name New Scenario
          </SimpleUSFDialogTitle>
          <SaveScenarioDialogContent>
            <ScenarioLegend>
              Names cannot be used twice in the same project.
            </ScenarioLegend>
            <DialogForm
              onSubmit={(e) => {
                e.preventDefault();
                this.handleSave();
              }}
            >
              {nameEle}
            </DialogForm>
          </SaveScenarioDialogContent>
          <SPWDialogActions className="small-bottom-padding">
            <SPWSaveButton88Width
              aria-label="Save Copied Scenario"
              onKeyPress={handleOnKeyPress as KeyboardEventHandler}
              onClick={() => {
                this.handleSave();
              }}
              disabled={disabled}
            />
            <SPWCancelButton88Width
              onClick={this.handleOnClose}
              customColor={colors.black}
            />
          </SPWDialogActions>
        </Dialog>
      );
    }

    if (!currentScenario) return null;

    const isTotalProgram =
      currentScenario?.calculatorType === TOTAL_PROGRAM_TYPE;
    if (isTotalProgram) {
      configurationEle = null;
    }

    const getDialogTitle = () => {
      if (duplicated) return 'Save As';

      return isUpdate
        ? `Rename ${isTotalProgram ? 'Total Program' : ''} Scenario`
        : `Save Scenario`;
    };

    return (
      <RenameScenarioDialog
        open={showDialog}
        onClose={this.handleOnClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        disableScrollLock
      >
        <SimpleUSFDialogTitle id="alert-dialog-title" className="big-padding">
          {getDialogTitle()}
        </SimpleUSFDialogTitle>
        <SaveScenarioDialogContent>
          <DialogForm
            onSubmit={(e) => {
              e.preventDefault();
              this.handleSave();
            }}
          >
            <Box marginTop={1} height="80px">
              {nameEle}
            </Box>
            {currentScenario?.id &&
              currentScenario?.floorId &&
              saveAsOperation === SaveAsOperation.copy && (
                <Box marginBottom={4}>
                  <FormControl variant="outlined" fullWidth>
                    <SelectShrunkenLabel>Floor Name</SelectShrunkenLabel>
                    <SPWBaseSelect
                      label="Floor Name"
                      value={selectedFloor || null}
                      MenuProps={{ classes: { paper: 'TopGapFloorSelector' } }}
                      renderItem={({ name }) => name}
                      onChange={this.handleFloorChange}
                      items={building?.floors || []}
                      getItemIsDisabled={({ totalFloorUsf }) =>
                        currentScenario.calculatorType ===
                          GROWTH_CALCULATORS.floorUsf && !totalFloorUsf
                      }
                      placeholder="Select a floor"
                    />
                  </FormControl>
                </Box>
              )}
            {currentScenario.calculatorType ===
              GROWTH_CALCULATORS.buildingHeadcountSeatcountDepartments &&
              saveAsOperation === SaveAsOperation.copy && (
                <Box marginBottom={4} width="100%">
                  <FormControl variant="outlined" fullWidth>
                    <SelectShrunkenLabel>Departments</SelectShrunkenLabel>
                    <SPWBaseSelect<IDepartment>
                      label="Departments"
                      placeholder="Select a department"
                      value={selectedDepartment}
                      style={{
                        width: '100%',
                      }}
                      onChange={(dept) =>
                        this.setState({ selectedDepartment: dept })
                      }
                      items={departments}
                      renderItem={(department) => (
                        <span>
                          <strong style={{ fontWeight: 500 }}>
                            {department.name}
                          </strong>
                          &nbsp;
                          {department.secondaryIdentifier || ''}
                        </span>
                      )}
                    />
                  </FormControl>
                </Box>
              )}
            {!isUpdate && configurationEle}
            {!isUpdate &&
              !isTotalProgram &&
              currentScenario?.id &&
              (saveAsOperation === SaveAsOperation.copy ||
                (saveAsOperation ===
                  SaveAsOperation.copyWithDifferentCalculator &&
                  calculatorType)) && (
                <CopyNotes
                  shouldCopyNotes={shouldCopyNotes}
                  setShouldCopyNotes={(val: boolean) =>
                    this.setState({ shouldCopyNotes: val })
                  }
                />
              )}
          </DialogForm>
        </SaveScenarioDialogContent>
        <SPWDialogActions
          className="small-bottom-padding"
          style={{ paddingTop: '5px' }}
        >
          <SPWSaveButton88Width
            tabIndex={0}
            onClick={this.handleSave}
            onKeyPress={handleOnKeyPress as KeyboardEventHandler}
            disabled={disabled}
            type="submit"
            aria-label="Save Copied Scenario"
          />
          <SPWCancelButton88Width
            onClick={this.handleOnClose}
            customColor={colors.black}
            data-testid="save-as-dialog-cancel-button"
          />
        </SPWDialogActions>
      </RenameScenarioDialog>
    );
  }
}

const mapStateToProps = (state: IAppState) => ({
  project: state.projectsReducer.currentProject,
  showDialog: state.calculatorReducer.showScenarioDialog,
  step: state.calculatorReducer.step,
  scenarios: selectScenarios(state),
  currentScenario: selectCurrentScenario(state),
  user: state.userReducer.user,
  duplicated: state.calculatorReducer.duplicated,
  building: pathOr(
    null,
    ['projectsReducer', 'currentProject', 'buildings', 0],
    state
  ),
  isUpdate: state.calculatorReducer.isUpdate,
  projectColors: state.projectsReducer.colors,
  totalPrograms: selectTotalPrograms(state),
  departments: selectProjectDepartments(state),
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      setCurrentView: setCurrentViewAction,
      setScenarioValue: setScenarioValueAction,
      saveScenario: saveScenarioAction,
      cloneScenario: cloneScenarioAction,
      setScenarioDialogStatus: setScenarioDialogStatusAction,
      setSummarySelectedIdType: setSummarySelectedIdTypeAction,
      setIsEditingSavedScenario: setIsEditingSavedScenarioAction,
      updateScenario: updateScenarioAction,
      saveUpdatingScenario: saveUpdatingScenarioAction,
      setUpdateScenarioDialog: setUpdateScenarioDialogAction,
      createTotalProgram: createTotalProgramAction,
    },
    dispatch
  );

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(SaveScenario)
);
